import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deviceFavoriteService } from '@/lib/device-favorite.service';
import { usePermissions } from '@/hooks/use-permissions';
import { deviceKeys } from '@/hooks/use-devices';
import type { DeviceFavorite } from '@/types';

export const favoriteKeys = {
  all: ['device-favorites'] as const,
};

/**
 * Display fields the caller already has, handed to the optimistic update so the
 * cached entry looks like the one the server will return. Without these the
 * sidebar cannot link a just-starred machine and stays a request behind the star.
 */
export type FavoriteTarget = Pick<
  DeviceFavorite,
  'device_id' | 'number' | 'serial_number' | 'status'
>;

/**
 * The one cache the whole feature reads.
 *
 * Every star in the product — devices list, device detail, the sidebar block —
 * renders from `machineIds`. There is deliberately no per-row or per-page fetch:
 * the set is the user's own and small, so one query serves all three surfaces
 * and the device-list payload never has to carry a favorite flag.
 */
export function useDeviceFavorites() {
  const { hasCan } = usePermissions();
  const query = useQuery<DeviceFavorite[]>({
    queryKey: favoriteKeys.all,
    queryFn: () => deviceFavoriteService.list(),
    // Minutes, not seconds: after the first load the gate below opens straight
    // from cache, so the wait is paid once per session rather than per page.
    staleTime: 5 * 60 * 1000,
    // Retry, but briefly. A failed favorites call must degrade to "no favorites"
    // rather than a broken devices page — and it used to do that permanently:
    // with retry disabled and a five-minute staleTime, one flaky response cost
    // the user every star, the chip and the sidebar until the cache expired.
    //
    // Retrying is safe here precisely because nothing blocks on this query. The
    // devices list renders from its own request and the stars fill in whenever
    // this resolves, so a retry costs nothing visible.
    retry: 2,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 4000),
  });

  // Built from `data?` on purpose — an errored or still-pending query yields an
  // empty set rather than undefined, so no consumer has to null-check.
  const machineIds = useMemo(
    () => new Set((query.data ?? []).map((f) => f.machine_id)),
    [query.data],
  );

  return {
    favorites: query.data ?? [],
    machineIds,
    isLoading: query.isLoading,
    /** Starring is device:write; reading and filtering are not. */
    canEdit: hasCan('device', 'write'),
  };
}

/**
 * Star / unstar, applied optimistically.
 *
 * The star is the feedback — a spinner on a one-bit toggle reads as breakage —
 * so the cache is updated before the request and rolled back if it fails.
 */
export function useToggleFavorite() {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ machineId, next }: { machineId: string; next: boolean; meta?: FavoriteTarget }) =>
      next ? deviceFavoriteService.add(machineId) : deviceFavoriteService.remove(machineId),

    onMutate: async ({ machineId, next, meta }) => {
      await qc.cancelQueries({ queryKey: favoriteKeys.all });
      const previous = qc.getQueryData<DeviceFavorite[]>(favoriteKeys.all);
      qc.setQueryData<DeviceFavorite[]>(favoriteKeys.all, (old = []) =>
        next
          ? old.some((f) => f.machine_id === machineId)
            ? old
            : [
                {
                  machine_id: machineId,
                  created_at: new Date().toISOString(),
                  // Carried from the caller so the optimistic entry is COMPLETE.
                  // The sidebar only links favorites that have a device_id, so an
                  // entry without one leaves the star filled while the sidebar
                  // stays empty until the round-trip lands — two surfaces
                  // disagreeing for the whole duration of the request.
                  ...(meta ?? {}),
                },
                ...old,
              ]
          : old.filter((f) => f.machine_id !== machineId),
      );
      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(favoriteKeys.all, ctx.previous);
    },

    onSettled: () => {
      // Reconcile with the server's row — the optimistic entry carries whatever
      // the caller had, which is enough to render but is not authoritative.
      qc.invalidateQueries({ queryKey: favoriteKeys.all });
      // The Favorites filter and the favorites-first sort are both server-side,
      // so a starred machine changes which rows and which order come back.
      qc.invalidateQueries({ queryKey: deviceKeys.lists() });
      qc.invalidateQueries({ queryKey: deviceKeys.stats() });
    },
  });

  const toggle = useCallback(
    (machineId: string, currentlyStarred: boolean, meta?: FavoriteTarget) =>
      mutation.mutate({ machineId, next: !currentlyStarred, meta }),
    [mutation],
  );

  return { toggle, isPending: mutation.isPending };
}
